import { track, type Track, type TrackingProp } from "react-tracking";

/**
 * The analytics events a BT component may self-report.
 *
 * Replaces the legacy `BdsAnalyticsEvent` from `@buildertrend/components`. The
 * string values are the wire format consumed by Mixpanel/Sawmill, so they must
 * not be renamed — only added to.
 */
export enum BTAnalyticsEvent {
  ButtonClick = "ButtonClick",
  MenuItemClick = "MenuItemClick",
  OpenMenu = "OpenMenu",
  PageView = "PageView",
  ModalOpen = "ModalOpen",
  ModalClose = "ModalClose",
  TabChange = "TabChange",
  LinkClick = "LinkClick",
  ValueChange = "ValueChange",
  OptionSelected = "OptionSelected",
  InputFocus = "InputFocus",
  InputBlur = "InputBlur",
  Search = "Search",
  CollapseToggled = "CollapseToggled",
  SortChange = "SortChange",
  DrawerClose = "DrawerClose",
  DrawerOpen = "DrawerOpen",
  InfiniteScroll = "InfiniteScroll",
  PopoverOpen = "PopoverOpen",
  PopoverClose = "PopoverClose",
  Hotkey = "HotKey",
  DragStart = "DragStart",
  DragEnd = "DragEnd",
  RowSave = "RowSave",
  RowEdit = "RowEdit",
  RowAdd = "RowAdd",
  RowRemove = "RowRemove",
}

/**
 * The tracking payload a BT component dispatches. Replaces the legacy
 * `IBdsTrackingData`; `page`/`template`/`organisism`/`molecule`/`atom`/`element`
 * are the atomic-design levels react-tracking merges down the context tree.
 */
export interface IBTTrackingData {
  page: string | undefined;
  template: string | undefined;
  organisism: string | undefined;
  molecule: string | undefined;
  atom: string | undefined;
  element: string | undefined;
  /**
   * A recognizable, stable identifier for analytics, in contrast to the
   * granular `testid`. Stripped from the payload by `trackBTEvent` after it is
   * folded into `uniqueId`.
   *
   * @example
   *  <li analyticId="lineItem" data-testid="TodoList-lineItem-0" />
   *  <li analyticId="lineItem" data-testid="TodoList-lineItem-1" />
   */
  analyticId: string | undefined;
  /** The `data-testid`, granular enough for testing. */
  testid: string | undefined;
  uniqueId?: string | undefined;
  event: BTAnalyticsEvent | undefined;
  value: string | number | undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  extraInfo: any | undefined;
}

export interface IBTTrackingProps {
  tracking?: TrackingProp<IBTTrackingData>;
  trackingEvent?: BTAnalyticsEvent;
  trackingHandled?: boolean;
}

/**
 * react-tracking's `track` HOC, typed to the BT tracking payload. Replaces the
 * legacy `BdsTrack`.
 */
export const BTTrack: Track<IBTTrackingData> = track;

/**
 * Per specification from analytics.
 * These filters remove unwanted characters from the uniqueId.
 *
 * ⚠️ ORDER IS BEHAVIOUR: only the LAST entry is ever applied. The legacy
 * `trackBdsEvent` looped over these assigning `uniqueId = original.replace(rx)`
 * each pass, so every filter re-read the *original* id and the last one
 * overwrote the rest. `applyAnalyticFilter` below reproduces that deliberately
 * rather than leaving it as an emergent property of a loop that reads
 * cumulative.
 *
 * So appending a filter here silently retires the current last one, and
 * reordering changes live Mixpanel/Sawmill values with no test failure beyond
 * the pinned cases in analytics.test.ts. Making them cumulative is likewise a
 * data change, not a cleanup: "cf-156774564" would start reporting as "cf".
 */
const ANALYTIC_FILTERS = [
  // removes all -\d+ from the uniqueId
  /-\d+/g,
  // removes all [.*? from the uniqueId — the effective filter (see above)
  /[a-zA-Z0-9]+\[.*?\./g,
];

/**
 * Strips analytics' unwanted characters from a `uniqueId`. Applies only the last
 * entry of {@link ANALYTIC_FILTERS}, preserving legacy `trackBdsEvent` output
 * exactly — see the note there before changing this.
 */
function applyAnalyticFilter(uniqueId: string): string {
  const effectiveFilter = ANALYTIC_FILTERS[ANALYTIC_FILTERS.length - 1];
  return effectiveFilter ? uniqueId.replace(effectiveFilter, "") : uniqueId;
}

/**
 * Globally handle tracking events from BT components. Replaces the legacy
 * `trackBdsEvent`.
 *
 * Analytics doesn't want the `analyticId`, but we do from a component
 * standpoint. Analytics wants the `uniqueId`, so we fill it from the
 * `analyticId` or the `testid`.
 */
export const trackBTEvent = (
  tracking: TrackingProp<IBTTrackingData> | undefined,
  event: Partial<IBTTrackingData>,
): void => {
  if (event.analyticId || event.testid) {
    const uniqueId = event.analyticId ?? event.testid;
    event.uniqueId =
      uniqueId === undefined ? uniqueId : applyAnalyticFilter(uniqueId);
    delete event.analyticId;
  }
  tracking?.trackEvent(event);
};
